import { db, storage } from '../config/firebase';
import {
    collection as firestoreCollection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { CoffeeCollection } from '../types/collection';

const COLLECTION_NAME = 'collections';

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const collectionService = {
    async addCollection(collectionData: CoffeeCollection, imageFile?: File) {
        try {
            let imageUrl;
            if (imageFile) {
                const storageRef = ref(storage, `collection-images/${Date.now()}_${imageFile.name}`);
                const uploadResult = await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(uploadResult.ref);
            }

            const docRef = await addDoc(firestoreCollection(db, COLLECTION_NAME), {
                ...collectionData,
                imageUrl,
                createdAt: new Date(),
                updatedAt: new Date()
            });

            return { id: docRef.id, ...collectionData, imageUrl };
        } catch (error) {
            throw new Error('Không thể thêm tuyển tập: ' + error);
        }
    },

    async getAllCollections() {
        const querySnapshot = await getDocs(firestoreCollection(db, COLLECTION_NAME));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as CoffeeCollection[];
    },

    async getCollectionById(id: string) {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as CoffeeCollection;
        }
        return null;
    },

    async updateCollection(id: string, collectionData: Partial<CoffeeCollection>, newImageFile?: File) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const currentDoc = await getDoc(docRef);

            if (!currentDoc.exists()) {
                throw new Error('Không tìm thấy tuyển tập');
            }

            let updateData = {
                ...collectionData,
                updatedAt: new Date()
            };

            if (newImageFile) {
                const oldImageUrl = currentDoc.data().imageUrl;
                if (oldImageUrl && isFirebaseStorageUrl(oldImageUrl)) {
                    try {
                        const oldImageRef = ref(storage, oldImageUrl);
                        await deleteObject(oldImageRef);
                        console.log(`Successfully deleted old collection image: ${oldImageUrl}`);
                    } catch (error) {
                        console.error(`Error deleting old collection image ${oldImageUrl}:`, error);
                    }
                } else if (oldImageUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${oldImageUrl}`);
                }

                const storageRef = ref(storage, `collection-images/${Date.now()}_${newImageFile.name}`);
                const uploadResult = await uploadBytes(storageRef, newImageFile);
                const newImageUrl = await getDownloadURL(uploadResult.ref);

                updateData = {
                    ...updateData,
                    imageUrl: newImageUrl
                };
            }

            await updateDoc(docRef, updateData);
            return { id, ...updateData };
        } catch (error) {
            throw new Error('Không thể cập nhật tuyển tập: ' + error);
        }
    },

    async deleteCollection(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const imageUrl = data.imageUrl;
                
                // Xóa ảnh từ Firebase Storage nếu tồn tại
                if (imageUrl && isFirebaseStorageUrl(imageUrl)) {
                    try {
                        const imageRef = ref(storage, imageUrl);
                        await deleteObject(imageRef);
                        console.log(`Successfully deleted collection image: ${imageUrl}`);
                    } catch (error) {
                        console.error(`Error deleting collection image ${imageUrl}:`, error);
                        // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                    }
                } else if (imageUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${imageUrl}`);
                }

                console.log(`Storage cleanup for collection ID ${id}: ${imageUrl ? (isFirebaseStorageUrl(imageUrl) ? '1 image deleted' : '1 non-Firebase URL skipped') : 'no image to delete'}`);
            }

            // Xóa document từ Firestore
            await deleteDoc(docRef);
            console.log(`Successfully deleted collection document with ID: ${id}`);
        } catch (error) {
            console.error(`Error deleting collection with ID ${id}:`, error);
            throw new Error('Không thể xóa tuyển tập: ' + error);
        }
    }
}; 